import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import type { ConversationItem, Role } from '@chatapp-inbox/shared';
import { useAssignConversation } from '../conversation';
import { AdminAssignControls } from './AdminAssignControls';
import { Button } from './Button';
import { StatusRegion } from './StatusRegion';
import { useStatusMessage } from './use-status-message';
import shared from './shared.module.css';
import styles from './AssignmentSection.module.css';

type AssignAction =
  { kind: 'claim' } | { kind: 'release' } | { kind: 'assign'; userId: string; name: string };

const successText = (action: AssignAction): string => {
  if (action.kind === 'claim') return 'Conversa assumida por você.';
  if (action.kind === 'release') return 'Conversa liberada.';
  return `Conversa atribuída a ${action.name}.`;
};

type FocusTarget = 'heading' | 'select' | 'primary' | 'keep';

/**
 * Seção "Responsável": quem cuida da conversa e o que ESTE usuário pode fazer com isso.
 * Atendente: assume a conversa sem dono e libera a sua (com confirmação); a de outra pessoa é só
 * leitura. Administrador: atribui a qualquer usuário ativo, assume e libera.
 *
 * Quem remonta esta seção por conversa (`key`) garante que confirmações e avisos não passem de uma
 * conversa para a outra.
 */
export function AssignmentSection({
  conversation,
  viewer,
}: {
  conversation: ConversationItem;
  viewer: { id: string; role: Role };
}): ReactElement {
  const promptId = useId();
  const { assign, pending, error } = useAssignConversation(conversation.id);
  const status = useStatusMessage();
  const assignee = conversation.assignedTo;
  const isMine = assignee?.id === viewer.id;
  const readOnly = viewer.role === 'agent' && assignee !== null && !isMine;

  const [confirmingRelease, setConfirmingRelease] = useState(false);
  const [lastAction, setLastAction] = useState<AssignAction | null>(null);
  const [wasPending, setWasPending] = useState(false);
  const [focusRequest, setFocusRequest] = useState<{ target: FocusTarget; n: number } | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const keepRef = useRef<HTMLButtonElement>(null);

  const requestFocus = (target: FocusTarget): void =>
    setFocusRequest((previous) => ({ target, n: (previous?.n ?? 0) + 1 }));

  // O gancho de atribuição não avisa "deu certo": o fim da operação é visto aqui, na passagem de
  // "em andamento" para "parada". Ajustar o estado durante a renderização (e não num efeito)
  // evita um quadro com o estado velho.
  if (pending !== wasPending) {
    setWasPending(pending);
    if (!pending && lastAction) {
      setConfirmingRelease(false);
      if (error) {
        // Só a confirmação some com o erro (os demais botões ficam onde estavam, com o foco): o foco
        // volta ao botão de liberar, ou ao título se ele já não existe (a conversa mudou de dono).
        if (confirmingRelease) requestFocus('primary');
      } else {
        status.announce(successText(lastAction));
        // O botão clicado quase sempre deixou de existir (assumir vira liberar): o foco vai para um
        // lugar que continua lá. O administrador segue no seletor, pronto para a próxima atribuição.
        requestFocus(viewer.role === 'admin' ? 'select' : 'heading');
      }
    }
  }

  useEffect(() => {
    if (!focusRequest) return;
    const refs = { heading: headingRef, select: selectRef, primary: primaryRef, keep: keepRef };
    const wanted = refs[focusRequest.target].current;
    const target =
      wanted && !(wanted instanceof HTMLSelectElement && wanted.disabled) ? wanted : null;
    (target ?? headingRef.current)?.focus();
  }, [focusRequest]);

  const run = (action: AssignAction): void => {
    if (pending) return;
    // O aviso do resultado anterior ("assumida por você") não pode ficar ao lado do desta tentativa.
    status.announce('');
    setLastAction(action);
    if (action.kind === 'claim') assign();
    else if (action.kind === 'release') assign(null);
    else assign(action.userId);
  };

  return (
    <section className={shared.section}>
      <h3 ref={headingRef} tabIndex={-1} className={shared.heading}>
        Responsável
      </h3>

      {/* Conversa de outra pessoa, vista por atendente: a frase de leitura abaixo já diz quem é. */}
      {!readOnly && (
        <p className={styles.current}>
          {!assignee ? (
            <span className={styles.none}>Sem responsável</span>
          ) : isMine ? (
            <span className={styles.you}>Você</span>
          ) : (
            <span dir="auto" className={styles.person}>
              {assignee.name}
            </span>
          )}
        </p>
      )}

      {viewer.role === 'admin' ? (
        <AdminAssignControls
          conversation={conversation}
          viewerId={viewer.id}
          busy={pending}
          selectRef={selectRef}
          onAssign={(userId, name) => run({ kind: 'assign', userId, name })}
          onClaim={() => run({ kind: 'claim' })}
          onRelease={() => run({ kind: 'release' })}
        />
      ) : !assignee ? (
        <div className={styles.actions}>
          <Button
            ref={primaryRef}
            variant="primary"
            busy={pending}
            onClick={() => run({ kind: 'claim' })}
          >
            Assumir conversa
          </Button>
        </div>
      ) : isMine ? (
        confirmingRelease ? (
          <div role="group" aria-labelledby={promptId} className={styles.confirm}>
            <p id={promptId} className={styles.confirmText}>
              Liberar esta conversa?
            </p>
            <p className={shared.hint}>Ela volta para a fila “Não atribuídas”.</p>
            <div className={styles.actions}>
              <Button variant="primary" busy={pending} onClick={() => run({ kind: 'release' })}>
                Sim, liberar
              </Button>
              <Button
                ref={keepRef}
                busy={pending}
                onClick={() => {
                  setConfirmingRelease(false);
                  requestFocus('primary');
                }}
              >
                Não
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.actions}>
            <Button
              ref={primaryRef}
              onClick={() => {
                // "Conversa assumida por você." não pode ficar ao lado da pergunta "Liberar?": o aviso
                // era sobre a ação anterior, e a pessoa já está decidindo a seguinte.
                status.announce('');
                setConfirmingRelease(true);
                // O botão clicado vai ser trocado pela pergunta: o foco vai para a resposta segura.
                requestFocus('keep');
              }}
            >
              Liberar conversa
            </Button>
          </div>
        )
      ) : (
        <p className={shared.hint}>
          Com{' '}
          <strong dir="auto" className={styles.person}>
            {assignee.name}
          </strong>
          . Só a pessoa responsável ou um administrador pode alterar.
        </p>
      )}

      <StatusRegion message={status.message} />
      {error && (
        <p role="alert" className={shared.alert}>
          {error}
        </p>
      )}
    </section>
  );
}
